import React from 'react';
import { Phone, ExternalLink } from 'lucide-react';
import PrintButton from '../components/PrintButton';
import SEO from '../components/SEO';

const URGENT = [
  {
    id: 'emergency',
    name: '999',
    label: 'Emergency',
    body:
      'If you are in immediate danger or have just been assaulted. If you cannot speak, press 55 to alert the operator silently.',
    phone: '999',
  },
  {
    id: 'police',
    name: '101',
    label: 'Police non-emergency',
    body: 'To report or ask for advice when it is not happening right now.',
    phone: '101',
  },
  {
    id: 'samaritans',
    name: '116 123',
    label: 'Samaritans',
    body: 'Free, 24/7 — for anyone struggling, in distress, or having thoughts of self-harm.',
    phone: '116123',
  },
];

const SPECIALIST = [
  {
    id: 'rapecrisis',
    name: 'Rape Crisis England & Wales',
    body:
      '24/7 free, confidential specialist support line for anyone 16+ affected by sexual violence, at any point in their life.',
    phone: '08088029999',
    phoneLabel: '0808 802 9999',
    url: 'https://rapecrisis.org.uk',
  },
  {
    id: 'newpathways',
    name: 'New Pathways',
    body:
      'Wales-based charity offering counselling, ISVA support and SARC services for survivors of all genders.',
    phone: '01685379310',
    phoneLabel: '01685 379 310',
    url: 'https://newpathways.org.uk',
  },
  {
    id: 'survivorstrust',
    name: 'The Survivors Trust',
    body:
      'UK-wide directory of specialist agencies. Helpline can refer you to your nearest member service.',
    phone: '08088010818',
    phoneLabel: '0808 801 0818',
    url: 'https://thesurvivorstrust.org',
  },
  {
    id: 'victimsupport',
    name: 'Victim Support',
    body:
      'Free, confidential help for anyone affected by crime — whether or not you have reported.',
    phone: '08081689111',
    phoneLabel: '0808 168 9111',
    url: 'https://www.victimsupport.org.uk',
  },
  {
    id: 'galop',
    name: 'Galop',
    body: 'LGBT+ specialist support for survivors of sexual violence, domestic abuse and hate crime.',
    phone: '08009995428',
    phoneLabel: '0800 999 5428',
    url: 'https://galop.org.uk',
  },
  {
    id: 'safeline',
    name: 'Safeline',
    body:
      "National helpline supporting survivors of sexual abuse and rape — including a men's helpline.",
    phone: '08088005005',
    phoneLabel: '0808 800 5005',
    url: 'https://www.safeline.org.uk',
  },
  {
    id: 'sarc',
    name: 'SARCs — Sexual Assault Referral Centres',
    body:
      'Free medical care, emotional support and forensic examination — whether or not you decide to report.',
    url: 'https://www.nhs.uk/service-search/other-services/Rape-and-sexual-assault-referral-centres/LocationSearch/364',
  },
];

export default function HelpNow() {
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="help-now-screen">
      <SEO
        title="Get Help Now — UK Helplines"
        description="UK helplines you can contact right now — 999, 101, Samaritans 116 123, Rape Crisis 0808 802 9999, Refuge 0808 2000 247, SurvivorsUK, Mankind Initiative, Men's Advice Line. Free and confidential, for women, men, non-binary and LGBT+ survivors."
        path="/app/help-now"
      />
      <p className="eyebrow">GET HELP NOW</p>
      <h1
        className="font-heading mt-3"
        style={{ fontSize: 'clamp(28px, 4vw, 40px)', lineHeight: 1.1, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
      >
        You don&apos;t have to<br />do this on your own.
      </h1>
      <p
        className="font-body mt-4 max-w-xl"
        style={{ fontSize: 15, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}
      >
        Below are UK services you can contact right now. All of them are free. You don&apos;t have
        to know what to say — they will help you find the words.
      </p>

      <div className="mt-6 flex gap-2 flex-wrap">
        <PrintButton label="Print / Save this page" />
      </div>

      <div className="mt-10">
        <p className="eyebrow">RIGHT NOW</p>
        <h2 className="font-heading mt-2 mb-5" style={{ fontSize: 24, color: 'var(--tf-text)' }}>
          If you need urgent help
        </h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {URGENT.map((u) => (
            <div key={u.id} className="tf-card p-5" data-testid={`urgent-${u.id}`}>
              <p
                className="font-heading"
                style={{ fontSize: 32, color: 'var(--tf-purple)', letterSpacing: '-0.01em', lineHeight: 1 }}
              >
                {u.name}
              </p>
              <p
                className="font-body font-bold mt-2"
                style={{ fontSize: 12, letterSpacing: '0.05em', color: 'var(--tf-purple-dark)', textTransform: 'uppercase' }}
              >
                {u.label}
              </p>
              <p
                className="font-body mt-2.5"
                style={{ fontSize: 13, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
              >
                {u.body}
              </p>
              <a
                href={`tel:${u.phone}`}
                data-testid={`urgent-call-${u.id}`}
                className="inline-flex items-center gap-1.5 mt-4 px-3.5 py-2 rounded-full font-bold"
                style={{ background: 'var(--tf-purple)', color: '#fff', fontSize: 12.5, textDecoration: 'none' }}
              >
                <Phone size={13} /> Call {u.name}
              </a>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12">
        <p className="eyebrow">SPECIALIST SUPPORT</p>
        <h2 className="font-heading mt-2 mb-5" style={{ fontSize: 24, color: 'var(--tf-text)' }}>
          Free, confidential helplines
        </h2>
        <div className="flex flex-col gap-3">
          {SPECIALIST.map((s) => (
            <div key={s.id} className="tf-card p-5" data-testid={`spec-${s.id}`}>
              <h3 className="font-heading" style={{ fontSize: 18, color: 'var(--tf-text)' }}>
                {s.name}
              </h3>
              <p
                className="font-body mt-2"
                style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
              >
                {s.body}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {s.phone && (
                  <a
                    href={`tel:${s.phone}`}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full font-bold"
                    style={{ background: 'var(--tf-purple)', color: '#fff', fontSize: 12, textDecoration: 'none' }}
                  >
                    <Phone size={12} /> {s.phoneLabel}
                  </a>
                )}
                {s.url && (
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full font-bold"
                    style={{
                      background: 'var(--tf-bg)',
                      color: 'var(--tf-purple)',
                      fontSize: 12,
                      border: '1px solid var(--tf-purple-light)',
                      textDecoration: 'none',
                    }}
                  >
                    <ExternalLink size={12} /> Visit website
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
